/**
 * Contract this app builds against (implemented in apps/api-py by a parallel
 * agent, NOT here — see support-admin-proposal.md §4 and the Phase 0 build
 * brief for 869f0ev1j):
 *
 *   GET  /api/admin-auth/google/start     302 to Google
 *   GET  /api/admin-auth/google/finish    sets HttpOnly cookie
 *                                         `ff_admin_session`, 302 to /
 *   GET  /api/admin-auth/me               -> { email, role } or 401
 *   POST /api/admin-auth/logout
 *
 * `google/finish` (not `google/callback`, renamed server-side, 869f0ev1j
 * follow-up): Google's own redirect_uri still lands on the product's
 * existing OAuth callback, which 302s the browser to
 * `https://<admin host>/api/admin-auth/google/finish?code=…` — the SPA never
 * calls either of those two hops directly, this is documentation of what a
 * link the browser follows on its own actually does.
 *
 * The admin SPA and the admin-auth API are the SAME origin
 * (admin.dev.fastfrm.com on dev — its Ingress proxies /api/admin-auth and
 * /api/admin to api-py), so `credentials: 'include'` below is what carries
 * the HttpOnly session cookie on a plain same-origin request; it is not
 * doing cross-origin work.
 */

export type AdminRole = 'viewer' | 'support' | 'finance' | 'superadmin';

/** Feature flags the API derives per environment. Absent on an older API —
 * read it as `capabilities?.x === true`. */
export interface AdminCapabilities {
  onboarding_test_accounts: boolean;
  /** Usage reports / user-workflows viewer (see CAPABILITY_* in lib/roles.ts). */
  usage_reports_read?: boolean;
  workflows_read?: boolean;
}

export interface AdminMe {
  email: string;
  role: AdminRole;
  capabilities?: AdminCapabilities;
}

/**
 * `''` (same-origin) unless VITE_ADMIN_API_ORIGIN is set — which is now only
 * a LOCAL-DEV override (`vite dev` serves the SPA on its own port with no
 * Ingress in front of it to proxy /api/*, so a real origin has to be named
 * there). Every deployed build leaves it unset: admin.dev.fastfrm.com's own
 * Ingress proxies /api/admin-auth and /api/admin to api-py, so the SPA's own
 * origin already answers those paths. Trailing slash trimmed so callers can
 * join with a leading-`/` path unconditionally.
 */
export const ADMIN_API_ORIGIN = (
  (import.meta.env.VITE_ADMIN_API_ORIGIN as string | undefined) ?? ''
).replace(/\/+$/, '');

export function adminApiUrl(path: string): string {
  return `${ADMIN_API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

export const GOOGLE_START_URL = adminApiUrl('/api/admin-auth/google/start');

/**
 * GET /api/admin-auth/me. Resolves to `null` on 401 (no session / expired) —
 * that is the ordinary "not signed in" outcome and callers redirect to
 * /login, not a thrown error. Any other non-OK status throws, since it is not
 * a shape the login flow knows how to recover from on its own.
 */
export async function fetchMe(): Promise<AdminMe | null> {
  const res = await fetch(adminApiUrl('/api/admin-auth/me'), {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new Error(`GET /api/admin-auth/me failed: ${res.status}`);
  }
  return (await res.json()) as AdminMe;
}

export async function logout(): Promise<void> {
  const res = await fetch(adminApiUrl('/api/admin-auth/logout'), {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`POST /api/admin-auth/logout failed: ${res.status}`);
  }
}

/**
 * Support-admin rebuild Phase 1 (ClickUp 869f084w5 / 869f0ev1x / 869f0ev36) —
 * the v2 surface (apps/api-py/app/api/admin_password_reset.py,
 * admin_audit_viewer.py). Both are `ff_admin_session`-only, no fallback to a
 * legacy product session, so every call below needs `credentials: 'include'`
 * exactly like the admin-auth calls above.
 */

export class AdminApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export interface PasswordResetResult {
  delivered_to: string;
  expires_hint: string;
}

/**
 * POST /api/admin/v2/users/{id}/password-reset. The temporary password is
 * NEVER in this response (server-side contract, proposal §7 decision 3) —
 * only a masked destination and a hint come back; the caller never has the
 * password to leak in the first place.
 */
export async function resetUserPassword(
  userId: string,
  reason: string,
): Promise<PasswordResetResult> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/users/${encodeURIComponent(userId)}/password-reset`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string } & Partial<PasswordResetResult>;
  if (!res.ok) {
    throw new AdminApiError(body.error ?? `password-reset failed: ${res.status}`, res.status);
  }
  return body as PasswordResetResult;
}

export interface AuditLogRow {
  id: string;
  created_at: string;
  actor_admin_id: string | null;
  actor_email: string;
  action: string;
  target_kind: string;
  target_id: string | null;
  reason: string;
  payload: Record<string, unknown>;
  request_id: string | null;
}

export interface AuditLogPage {
  rows: AuditLogRow[];
  next_cursor: string | null;
}

/** Every filter GET /api/admin/v2/audit(/export.csv) accepts — `cursor` is
 * table-paging only, never passed to the CSV export (export.csv has no
 * pagination of its own, per its own 10k-row cap). */
export interface AuditLogFilters {
  actor?: string;
  action?: string;
  target?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

function auditQueryString(filters: AuditLogFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchAuditLog(filters: AuditLogFilters): Promise<AuditLogPage> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/audit${auditQueryString(filters)}`), {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new AdminApiError(`GET /api/admin/v2/audit failed: ${res.status}`, res.status);
  }
  return (await res.json()) as AuditLogPage;
}

/** The href for the CSV export button — a plain same-origin GET with
 * credentials carried by the browser's own cookie jar, so this needs no
 * fetch/blob dance; the link just needs the right query string. */
export function auditExportCsvUrl(filters: Omit<AuditLogFilters, 'cursor' | 'limit'>): string {
  return adminApiUrl(`/api/admin/v2/audit/export.csv${auditQueryString(filters)}`);
}

/**
 * GET /api/admin/v2/users(?q=&page=&limit=) and friends — support-admin
 * Phase 1 §1.1 (user search/lookup) and §1.4 (content history per user),
 * ClickUp 869f0ev1r / 869f0ev2a. Backed by apps/api-py/app/api/admin_users_v2.py,
 * open to any active admin role.
 */

export interface AdminUserOrg {
  id: string;
  name: string | null;
}

export interface AdminUserWorkspace {
  id: string;
  name: string | null;
}

export interface AdminUserPlan {
  id: string;
  name: string | null;
}

export interface AdminUserFlags {
  deleted: boolean;
  impersonation_allowed: boolean;
}

export interface AdminUserRow {
  id: string;
  email: string | null;
  name: string | null;
  created_at: string | null;
  auth_method: string | null;
  organization: AdminUserOrg | null;
  workspaces: AdminUserWorkspace[];
  role: string | null;
  plan: AdminUserPlan | null;
  /** The provider's word, not the plan's (869f48zh7): true only while a
   * subscription is live at Solidgate. A paid `plan` with `paying: false` is a
   * hand-activated or gifted plan — not a customer. */
  paying: boolean;
  platform: string;
  /** The newest tala_pipelines_runs.date_created of this profile — the "Last
   * generation" column; null when they never ran anything. */
  last_activity: string | null;
  credits_remaining: number | null;
  /** Messages sent to the chat agent, all time ("Messages" column). */
  messages_sent: number;
  /** Successful charged generations (images, videos, texts), all time, net of
   * refunds ("Generations" column). */
  content_created: number;
  /** Net credits spent on the agent and generations, all time; can be
   * fractional ("Credits spent" column). */
  credits_spent: number;
  /** Latest of sign-in, chat activity, generation and sign-up (ISO, never
   * null) — the "Last active" column. Not the same as `last_activity`. */
  last_active: string;
  flags: AdminUserFlags;
}

export interface AdminUserOrgHistoryEntry {
  organization_id: string;
  name: string | null;
  role: string | null;
  plan_name: string | null;
  joined_at: string | null;
}

/** The card does not carry the list-only activity counters. */
export interface AdminUserCard
  extends Omit<AdminUserRow, 'messages_sent' | 'content_created' | 'credits_spent' | 'last_active'> {
  organization_history: AdminUserOrgHistoryEntry[];
}

export interface AdminUsersSearchResult {
  users: AdminUserRow[];
  page: number;
  limit: number;
  /** Rows matching the search AND every filter (subscription, last active,
   * credits spent). */
  total: number;
  /** Rows matching the search alone — the denominator of the result line
   * ("235 of 2 066 users · Paying now", ClickUp 869f4c8yj). Equals `total`
   * when no filter is on. Absent from a bundle older than the API: the page
   * falls back to `total`. */
  total_unfiltered?: number;
}

export interface AdminContentRow {
  kind: 'pipeline_run' | 'node_run';
  id: string;
  date_created: string | null;
  status: string | null;
  workflow_name: string | null;
  error_message: string | null;
  /**
   * The TYPED failure reason (`tala_*_runs.error_class`) — `bad-input`,
   * `auth`, `provider-timeout`, `content-refused`, `render-no-template`,
   * `account-throttled`, ... Null on a successful row, and also null on some
   * failures (measured on dev 16.09.2026: 55 of 155 failed node runs carry
   * one), which is why the UI falls back to `error_message` rather than
   * showing an empty cell (ClickUp 869f084w5 §4: "for failed generations, the
   * reason for the error, where available").
   */
  error_class: string | null;
  credits_charged: number;
  media_key: string | null;
}

export interface AdminContentPage {
  rows: AdminContentRow[];
  next: string | null;
}

export async function adminGetJson<T>(path: string, init: { signal?: AbortSignal } = {}): Promise<T> {
  const res = await fetch(adminApiUrl(path), { credentials: 'include', signal: init.signal });
  if (!res.ok) {
    throw new Error(`GET ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/** `?subscription=` on GET /api/admin/v2/users (869f48zh7). Three sets with
 * explicit names because "has a subscription" is two different facts:
 * `paying` = live provider subscription (what Support means by "subscribers"),
 * `paid_plan` = any non-default plan, including ones activated by hand from
 * this console, `free` = the rest. */
export type SubscriptionFilter = 'paying' | 'paid_plan' | 'free';
export const SUBSCRIPTION_FILTERS: readonly SubscriptionFilter[] = ['paying', 'paid_plan', 'free'];

/** `?sort=`: newest profile first (the API default) or by one of the activity
 * columns. `?dir=` flips any of them. */
export type UsersSort =
  | 'created'
  | 'last_generation'
  | 'messages'
  | 'content'
  | 'credits_spent'
  | 'last_active';
export type SortDir = 'desc' | 'asc';

export interface SearchUsersRequest {
  q?: string;
  page?: number;
  limit?: number;
  subscription?: SubscriptionFilter;
  sort?: UsersSort;
  dir?: SortDir;
  /** Last active within the last N days (1..3650). */
  active_within_days?: number;
  /** Last active more than N days ago (1..3650). */
  inactive_days?: number;
  /** ISO timestamps; `active_from` inclusive, `active_to` EXCLUSIVE. */
  active_from?: string;
  active_to?: string;
  /** Inclusive bounds on credits spent. */
  credits_min?: number;
  credits_max?: number;
}

export async function searchUsers(
  params: SearchUsersRequest,
  init: { signal?: AbortSignal } = {},
): Promise<AdminUsersSearchResult> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.subscription) qs.set('subscription', params.subscription);
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  if (params.active_within_days) qs.set('active_within_days', String(params.active_within_days));
  if (params.inactive_days) qs.set('inactive_days', String(params.inactive_days));
  if (params.active_from) qs.set('active_from', params.active_from);
  if (params.active_to) qs.set('active_to', params.active_to);
  if (params.credits_min !== undefined) qs.set('credits_min', String(params.credits_min));
  if (params.credits_max !== undefined) qs.set('credits_max', String(params.credits_max));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminGetJson<AdminUsersSearchResult>(`/api/admin/v2/users${suffix}`, init);
}

/** Resolves to `null` on 404 — the caller renders a not-found state, not an error. */
export async function fetchUserCard(userId: string): Promise<AdminUserCard | null> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/users/${userId}`), {
    credentials: 'include',
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`GET /api/admin/v2/users/${userId} failed: ${res.status}`);
  }
  return (await res.json()) as AdminUserCard;
}

export async function fetchUserContent(
  userId: string,
  params: { cursor?: string; limit?: number } = {},
): Promise<AdminContentPage> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set('after', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminGetJson<AdminContentPage>(`/api/admin/v2/users/${userId}/content${suffix}`);
}

/**
 * GET /api/admin/v2/users/{id}/ledger — the FULL coin ledger of the user's
 * organization(s) (support ticket 28.09.2026). The Content tab lists RUNS, so
 * an agent turn (no run at all) or a charge whose workflow was deleted (the
 * ledger outlives its runs) never appeared there. Every admin role reads it;
 * the API returns ids and numbers only, never metadata or chat text.
 * `kind` is named once, server-side (app/models/ledger_kind.py).
 */
export type LedgerKind =
  | 'agent_turn'
  | 'node_run'
  | 'run'
  | 'dictation'
  | 'other_charge'
  | 'refund'
  | 'purchase'
  | 'purchase_reversal'
  | 'grant'
  | 'adjustment'
  | 'other';

export interface AdminLedgerRow {
  id: string;
  date_created: string | null;
  kind: LedgerKind;
  /** Signed, as stored: a debit is negative, a refund/purchase positive. */
  credits: number;
  source: string | null;
  type: string | null;
  model_key: string | null;
  node_run_id: string | null;
  pipeline_run_id: string | null;
  pipeline_id: string | null;
  execution_no: number | null;
  profile_id: string | null;
  organization_id: string | null;
  message_id: string | null;
  chat_id: string | null;
  /** What a complimentary (onboarding) charge would have cost. */
  waived_credits: number | null;
  /** null = the row has no run; false = the run row is gone (workflow deleted). */
  run_exists: boolean | null;
  by_this_user: boolean;
}

export interface AdminLedgerSummary {
  spent_agent: number;
  spent_generations: number;
  spent_other: number;
  spent_total: number;
  refunded: number;
  net_spent: number;
  purchased: number;
  purchase_reversed: number;
  granted: number;
  adjustments: number;
  waived: number;
  by_kind: Partial<Record<LedgerKind, { rows: number; credits: number }>>;
}

/**
 * A subscription's credits are the PLAN's limit per period, not a ledger row:
 * the ticket's organization had a paid trial and no `purchase` row at all. The
 * API sends the balance exactly as the product computes it
 * (app/http/credit_balance.resolve_org_credit_balance), per member organization.
 */
export interface AdminOrgBalance {
  period: { start: string; end: string };
  allowance: number | null;
  used: number;
  held: number;
  purchased: number;
  remaining: number | null;
  unlimited: boolean;
}

export interface AdminLedgerAllowance {
  organization_id: string;
  organization_name: string | null;
  /** null when the balance could not be computed for this organization. */
  balance: AdminOrgBalance | null;
}

export interface AdminLedgerPage {
  rows: AdminLedgerRow[];
  next: string | null;
  summary: AdminLedgerSummary;
  allowances: AdminLedgerAllowance[];
}

export async function fetchUserLedger(
  userId: string,
  params: { cursor?: string; limit?: number } = {},
): Promise<AdminLedgerPage> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set('after', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminGetJson<AdminLedgerPage>(`/api/admin/v2/users/${userId}/ledger${suffix}`);
}

/**
 * GET /api/admin/v2/users/{id}/chats and …/chats/{chat_id} — a user's chats
 * with the assistant (ClickUp 869f4c8wg). SUPERADMIN ONLY: the API answers
 * 403 to every other role (app/models/admin_role.py:ACTION_USER_CHATS_READ),
 * and every read leaves a strict audit receipt. This client only mirrors the
 * gate (`canReadUserChats` in lib/roles.ts) to hide the tab.
 */
export interface AdminChatRow {
  id: string;
  workspace_id: string | null;
  workspace_name: string | null;
  title: string | null;
  date_created: string | null;
  date_updated: string | null;
  complimentary_reason: string | null;
  message_count: number;
  user_message_count: number;
}

export interface AdminChatsPage {
  rows: AdminChatRow[];
  next: string | null;
}

export interface AdminChatAction {
  id: string | null;
  name: string | null;
  has_result: boolean;
}

export interface AdminChatAttachment {
  name: string | null;
  mime: string | null;
  /** A storage key — signed on demand through POST /api/admin/v2/media/sign. */
  key: string;
}

/** One transcript line (apps/api-py/app/http/chat_transcript.py): the
 * prose, the NAMED tool calls and the attachments — never a tool's
 * input/result or a workflow snapshot, which the API drops. */
export interface AdminChatMessage {
  id: string;
  role: string;
  date_created: string | null;
  text: string | null;
  actions: AdminChatAction[];
  attachments: AdminChatAttachment[];
  summary: string | null;
}

export interface AdminChatTranscriptPage {
  chat: AdminChatRow;
  rows: AdminChatMessage[];
  next: string | null;
}

export async function fetchUserChats(
  userId: string,
  params: { cursor?: string; limit?: number } = {},
): Promise<AdminChatsPage> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set('after', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminGetJson<AdminChatsPage>(`/api/admin/v2/users/${userId}/chats${suffix}`);
}

export async function fetchUserChat(
  userId: string,
  chatId: string,
  params: { cursor?: string; limit?: number } = {},
): Promise<AdminChatTranscriptPage> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set('after', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return adminGetJson<AdminChatTranscriptPage>(
    `/api/admin/v2/users/${userId}/chats/${chatId}${suffix}`,
  );
}

/** { key: signed url | null } — a `null` value means "not this user's media
 * (or signing failed)", never distinguished further (mirrors sign-media's own
 * contract: the response cannot be used to probe which keys exist). */
export async function signUserMedia(
  userId: string,
  keys: string[],
): Promise<Record<string, string | null>> {
  const res = await fetch(adminApiUrl('/api/admin/v2/media/sign'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ user_id: userId, keys }),
  });
  if (!res.ok) {
    throw new Error(`POST /api/admin/v2/media/sign failed: ${res.status}`);
  }
  const body = (await res.json()) as { urls: Record<string, string | null> };
  return body.urls;
}


/**
 * /api/admin/v2/admin-accounts (ClickUp 869f0ev3c access model): who may use
 * this console and with which role. Any role may list; only a superadmin may
 * add, change a role or deactivate — the API enforces that, this client only
 * mirrors it to hide controls.
 */
export interface AdminAccount {
  id: string;
  email: string;
  role: AdminRole;
  active: boolean;
  disabled_at: string | null;
  created_at: string | null;
}

export interface AdminAccountsResponse {
  accounts: AdminAccount[];
  roles: AdminRole[];
}

async function adminAccountsError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new AdminApiError(body.error ?? `${fallback}: ${res.status}`, res.status);
}

export async function listAdminAccounts(): Promise<AdminAccountsResponse> {
  const res = await fetch(adminApiUrl('/api/admin/v2/admin-accounts'), { credentials: 'include' });
  if (!res.ok) return adminAccountsError(res, 'admin-accounts list failed');
  return (await res.json()) as AdminAccountsResponse;
}

export async function createAdminAccount(input: {
  email: string;
  role: AdminRole;
  reason: string;
}): Promise<AdminAccount> {
  const res = await fetch(adminApiUrl('/api/admin/v2/admin-accounts'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return adminAccountsError(res, 'admin-account create failed');
  return (await res.json()) as AdminAccount;
}

export async function updateAdminAccount(
  id: string,
  input: { role?: AdminRole; active?: boolean; reason: string },
): Promise<AdminAccount> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/admin-accounts/${encodeURIComponent(id)}`), {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return adminAccountsError(res, 'admin-account update failed');
  return (await res.json()) as AdminAccount;
}

/**
 * GET /api/admin/v2/grants — the grants panel (ClickUp 869f084w5 §4,
 * apps/api-py/app/api/admin_grants.py). A PROJECTION of the same append-only
 * `admin_audit_log` the audit viewer reads, with the five things the request
 * named lifted out of `payload` into columns: who / when / for which platform
 * / how much currency or which plan or which benefit / for how long.
 */

export type EntitlementPlatform = 'WEB' | 'APP';

export interface GrantRow {
  id: string;
  actor_email: string | null;
  actor_admin_id: string | null;
  created_at: string;
  platform: EntitlementPlatform | null;
  kind: string;
  action: string;
  amount: number | null;
  plan_id: string | null;
  benefit_key: string | null;
  valid_until: string | null;
  target_kind: string | null;
  target_id: string | null;
  reason: string;
  payload: Record<string, unknown>;
}

export interface GrantsPage {
  rows: GrantRow[];
  next_cursor: string | null;
}

export interface GrantsFilters {
  actor?: string;
  target?: string;
  platform?: string;
  kind?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export async function fetchGrants(filters: GrantsFilters): Promise<GrantsPage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  const res = await fetch(adminApiUrl(`/api/admin/v2/grants${qs ? `?${qs}` : ''}`), {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new AdminApiError(`GET /api/admin/v2/grants failed: ${res.status}`, res.status);
  }
  return (await res.json()) as GrantsPage;
}

/**
 * Change of login email / account transfer (ClickUp 869f4a4a0) — the v2
 * routes in apps/api-py/app/api/admin_account_transfer.py. Same session
 * contract as the password reset above; same `AdminApiError` on a refusal so
 * the dialogs can show the server's own sentence (the 409 that says "use
 * transfer" is the one a support agent must read, not a generic failure).
 */

export interface ChangeEmailResult {
  ok: true;
  /** `email` — the account kept its password; `google` — a Google-only
   * account was handed a temporary password by the reset mail. */
  mode: 'email' | 'google';
  old_email: string | null;
  new_email: string;
  unlinked_identities: { provider: string; provider_id: string }[] | null;
  temp_password_delivered_to: string | null;
  notices: { old: string; new: string };
  warning: string | null;
}

async function postAdminJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(adminApiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) {
    throw new AdminApiError(payload.error ?? `request failed: ${res.status}`, res.status);
  }
  return payload;
}

export function changeUserEmail(
  userId: string,
  newEmail: string,
  reason: string,
): Promise<ChangeEmailResult> {
  return postAdminJson(`/api/admin/v2/users/${encodeURIComponent(userId)}/change-email`, {
    new_email: newEmail,
    reason,
  });
}

export interface TransferPreviewOrg {
  organization_id: string;
  name: string | null;
  role: string | null;
  member_count: number;
  plan_name: string | null;
  target_already_member_as: string | null;
}

export interface TransferPreviewWorkspace {
  workspace_id: string;
  name: string | null;
  organization_id: string | null;
  role: string | null;
  credits_remaining: number | null;
  target_already_member_as: string | null;
}

export interface TransferPreviewRow {
  table: string;
  column: string;
  rows: number;
  duplicates_dropped: number;
}

export interface TransferPreviewParty {
  profile_id: string;
  email: string | null;
  pipelines: number;
  runs: number;
  ledger_rows: number;
}

export interface TransferPreview {
  source: TransferPreviewParty;
  target: TransferPreviewParty;
  organizations: TransferPreviewOrg[];
  workspaces: TransferPreviewWorkspace[];
  rows: TransferPreviewRow[];
  blocked_by_sole_admin_of_org: string | null;
  source_outcome: string;
}

export interface TransferResult {
  ok: true;
  target_profile_id: string;
  moved: {
    repointed: Record<string, number>;
    duplicates_dropped: Record<string, number>;
    source_login_closed: boolean;
  };
  source_erasure: { result: 'ok' | 'failed'; profile?: string; error?: string };
}

export function previewTransfer(
  userId: string,
  targetProfileId: string,
  reason: string,
): Promise<TransferPreview> {
  return postAdminJson(`/api/admin/v2/users/${encodeURIComponent(userId)}/transfer/preview`, {
    target_profile_id: targetProfileId,
    reason,
  });
}

export function transferAccount(
  userId: string,
  targetProfileId: string,
  reason: string,
  confirm: string,
): Promise<TransferResult> {
  return postAdminJson(`/api/admin/v2/users/${encodeURIComponent(userId)}/transfer`, {
    target_profile_id: targetProfileId,
    reason,
    confirm,
  });
}

/**
 * GET /api/admin/v2/reports/usage-mix?days= (ClickUp 869f4c9vb) — what
 * people run, as COUNTS: by the builtin template a workflow descends from
 * and by what the workflow makes. SUPERADMIN ONLY (`admin.user_reports.read`);
 * the body never names a person. Backed by apps/api-py/app/api/admin_reports.py.
 */
export interface UsageMixRow {
  key: string;
  runs: number;
  /** A fraction of the window's runs, 0..1. */
  share: number;
  /** Distinct users behind the rows — only on the origin cut. */
  users?: number;
}

export interface UsageMixReport {
  days: number;
  since: string;
  runs: number;
  users: number;
  by_origin: UsageMixRow[];
  by_output: UsageMixRow[];
}

export async function fetchUsageMix(days: number): Promise<UsageMixReport> {
  return adminGetJson<UsageMixReport>(`/api/admin/v2/reports/usage-mix?days=${days}`);
}

/**
 * Spend report (ClickUp 1amz0xe2evr): what generations cost US (provider cost,
 * not the price a user paid). Open to every console role (`viewer` and up).
 * Users are aggregates only — no email, id or workflow title; only the team's
 * own accounts are named. Backed by apps/api-py/app/api/admin_reports.py.
 */
export type SpendSegment = 'team' | 'user' | 'test';
export type SpendTier = 'casual' | 'regular' | 'power';

export interface SpendTeamMonthlyRow {
  month: string;
  account: string;
  ops: number;
  cost_usd: number;
}

export interface SpendTeamWorkflowRow {
  account: string;
  workflow: string;
  ops: number;
  cost_usd: number;
}

export interface SpendByModelRow {
  segment: 'team' | 'user';
  model: string;
  ops: number;
  cost_usd: number;
}

export interface SpendByKindRow {
  month: string;
  segment: SpendSegment;
  kind: 'agent_chat' | 'nodes' | 'other';
  ops: number;
  cost_usd: number;
}

export interface SpendUsersMonthlyRow {
  month: string;
  signups: number;
  active_users: number;
  ops: number;
  cost_usd: number;
  /** Share (0..1) of the month's active users also active the next month; null when unknown. */
  retained_next_month: number | null;
}

export interface SpendUserTierRow {
  month: string;
  tier: SpendTier;
  users: number;
  cost_usd: number;
  cost_per_user: number;
  ops_per_user: number;
}

export interface SpendDailyRow {
  day: string;
  segment: SpendSegment;
  ops: number;
  cost_usd: number;
}

export interface SpendReport {
  as_of: string;
  months: number;
  team_monthly: SpendTeamMonthlyRow[];
  team_by_workflow: SpendTeamWorkflowRow[];
  by_model: SpendByModelRow[];
  by_kind: SpendByKindRow[];
  users_monthly: SpendUsersMonthlyRow[];
  user_tiers: SpendUserTierRow[];
  daily: SpendDailyRow[];
}

export async function fetchSpend(months: number): Promise<SpendReport> {
  return adminGetJson<SpendReport>(`/api/admin/v2/reports/spend?months=${months}`);
}
